import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useFacilityUser } from "../../hooks/useFacilityUser";
import { paymentApi } from "../../services/payment.api";
import {
  PAYMENT_SESSION_KEY,
  PaymentSessionContext,
  PaymentStatus,
  PaymentStatusResponse,
  TERMINAL_STATUSES,
} from "../../types/payment";
import { findOpenPaymentContext } from "../../utils/payment.utils";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100;

type PageState =
  | { kind: "loading" }
  | { kind: "polling"; status: PaymentStatus; events: PaymentStatusResponse["events"] }
  | { kind: "completed"; response: PaymentStatusResponse }
  | { kind: "failed"; status: PaymentStatus }
  | { kind: "error"; message: string };

export const PaymentCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const facilityId = useFacilityUser()?.healthcareFacility?.id;
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const pollCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem(PAYMENT_SESSION_KEY);

    let storedCtx: PaymentSessionContext | null = null;
    if (raw) {
      try {
        storedCtx = JSON.parse(raw) as PaymentSessionContext;
      } catch {
        setState({ kind: "error", message: t("payment.callback.invalidContext") });
        return;
      }
    }

    // Guards against React 18 StrictMode's dev-only double-invoke of effects:
    // an in-flight poll from a cleaned-up effect instance must not act on a
    // stale response (which would render the wrong screen or schedule an
    // extra poll loop).
    let active = true;

    // The licence page re-checks the pending invoice for an open payment itself.
    const goToLicencePage = () => navigate("/manager/licence", { replace: true });

    const poll = async (ctx: PaymentSessionContext) => {
      if (pollCount.current >= MAX_POLLS) {
        setState({ kind: "error", message: t("payment.callback.timeout") });
        return;
      }
      pollCount.current += 1;

      try {
        const response = await paymentApi.getInvoicePaymentStatus(
          ctx.invoiceId,
          ctx.paymentIntentId
        );

        if (TERMINAL_STATUSES.includes(response.status)) {
          // Clear the payment context as soon as the outcome is known, even
          // if this effect instance was superseded — otherwise a stale
          // intent lingers in sessionStorage and gets re-processed on every
          // later, unrelated visit to the licence page.
          stopPolling();
          sessionStorage.removeItem(PAYMENT_SESSION_KEY);
          if (!active) return;
          if (response.status === "COMPLETED") {
            setState({ kind: "completed", response });
          } else {
            setState({ kind: "failed", status: response.status });
          }
          return;
        }

        if (!active) return;
        setState({ kind: "polling", status: response.status, events: response.events });
      } catch (error) {
        // MED-1321: the intent is unknown to the backend (stale or foreign
        // context) — there is nothing to wait for.
        if ((error as { code?: string } | undefined)?.code === "PAYMENT_INTENT_NOT_FOUND") {
          stopPolling();
          sessionStorage.removeItem(PAYMENT_SESSION_KEY);
          if (active) goToLicencePage();
          return;
        }
        // Non-fatal: keep polling on transient errors, stop on max retries
      }

      if (!active) return;
      timerRef.current = setTimeout(() => void poll(ctx), POLL_INTERVAL_MS);
    };

    if (storedCtx) {
      void poll(storedCtx);
    } else {
      // MED-1321: no context in this tab (e.g. PayDunya's return URL opened in
      // a new tab) — follow the pending invoice's open payment instead, or hand
      // over to the licence page when there is none.
      void findOpenPaymentContext(facilityId).then((openCtx) => {
        if (!active) return;
        if (openCtx) void poll(openCtx);
        else goToLicencePage();
      });
    }

    return () => {
      active = false;
      stopPolling();
    };
  }, [t, stopPolling, navigate, facilityId]);

  const goToLicence = () => navigate("/manager/licence");

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm text-center">
        {state.kind === "loading" && (
          <>
            <Spinner />
            <p className="mt-4 text-sm text-neutral-600">{t("payment.callback.loading")}</p>
          </>
        )}

        {state.kind === "polling" && (
          <>
            <Spinner />
            <p className="mt-4 text-base font-semibold text-neutral-800">
              {t("payment.callback.polling")}
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              {t(`payment.status.${state.status.toLowerCase()}`, state.status)}
            </p>
            {state.events.length > 0 && (
              <ul className="mt-4 space-y-2 text-left">
                {state.events.map((evt, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-neutral-600">
                    <span className="h-2 w-2 rounded-full bg-primary-400 flex-shrink-0" />
                    <span>{t(`payment.status.${evt.status.toLowerCase()}`, evt.status)}</span>
                    <span className="ml-auto text-neutral-400 text-xs">
                      {new Date(evt.createdAt).toLocaleTimeString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {state.kind === "completed" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-100">
              <span className="text-2xl">✓</span>
            </div>
            <h2 className="text-xl font-bold text-success-700">
              {t("payment.callback.success.title")}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {t("payment.callback.success.message", {
                amount: state.response.amount.toLocaleString(),
                currency: state.response.currency,
              })}
            </p>
            <button
              onClick={goToLicence}
              className="mt-6 w-full rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
            >
              {t("payment.callback.success.cta")}
            </button>
          </>
        )}

        {state.kind === "failed" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger-100">
              <span className="text-2xl">✕</span>
            </div>
            <h2 className="text-xl font-bold text-danger-700">
              {state.status === "CANCELLED"
                ? t("payment.callback.failed.cancelled.title")
                : t("payment.callback.failed.title")}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {state.status === "CANCELLED"
                ? t("payment.callback.failed.cancelled.message")
                : t("payment.callback.failed.message")}
            </p>
            <button
              onClick={goToLicence}
              className="mt-6 w-full rounded-lg border border-neutral-200 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              {t("payment.callback.failed.cta")}
            </button>
          </>
        )}

        {state.kind === "error" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-warning-100">
              <span className="text-2xl">⚠</span>
            </div>
            <h2 className="text-xl font-bold text-warning-700">
              {t("payment.callback.error.title")}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">{state.message}</p>
            <button
              onClick={goToLicence}
              className="mt-6 w-full rounded-lg border border-neutral-200 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
            >
              {t("payment.callback.error.cta")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

function Spinner() {
  return (
    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
  );
}
